/**
 * Billing health detail panel — shown when a row in the health queue is
 * clicked. Per docs/specs/billing.md §5.3 + §7 cost attribution breakdown.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-22-v1
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Chip } from '../../components/chip';
import { RoleButton } from '../../components/role-gate';
import { useToast } from '../../components/toast';
import { useBillingHealthQueue, useUser, useUserBillingHealth } from '../../../lib/api';
import { apiPost, getHttpErrorMessage } from '../../../lib/http';
import type { BillingIssueKind } from '../../../lib/types';

type BillingAction = 'retry_payment' | 'force_sync' | 'warn' | 'waive' | 'refund' | 'downgrade';

type BillingActionResponse = {
    success: boolean;
    action: BillingAction;
    user_id: string;
    issue?: string | null;
    error?: string | null;
    status?: string | null;
    track_count?: number;
    track_limit?: number;
    synced_count?: number;
};

interface BillingHealthDetailProps {
    rowId: string;
    onClose: () => void;
}

const fmtMoney = (n: number) =>
    n < 0
        ? `−$${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
        : `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const issueTone = (i: BillingIssueKind) => {
    if (i === 'payment_failed' || i === 'dispute' || i === 'multi_failed') return 'danger';
    if (i === 'plan_drift' || i === 'overage') return 'warning';
    if (i === 'card_expiring' || i === 'trial_ending') return 'info';
    return 'muted';
};

function actionScope(action: BillingAction): 'support_ok' | 'owner_only' {
    return action === 'refund' || action === 'downgrade' ? 'owner_only' : 'support_ok';
}

function actionLabel(action: BillingAction) {
    switch (action) {
        case 'retry_payment':
            return 'Retry payment';
        case 'force_sync':
            return 'Force sync';
        case 'warn':
            return 'Warn overage';
        case 'waive':
            return 'Waive';
        case 'refund':
            return 'Refund';
        case 'downgrade':
            return 'Downgrade';
        default:
            return 'Action';
    }
}

function resolveQueueUserId(rowId: string | null): string | null {
    if (!rowId) return null;
    const idx = rowId.indexOf(':');
    return idx > 0 ? rowId.slice(idx + 1) : null;
}

export function BillingHealthDetail({ rowId, onClose }: BillingHealthDetailProps) {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const [inflight, setInflight] = useState<BillingAction | null>(null);

    const queue = useBillingHealthQueue();
    const row = queue.data?.find((r) => r.id === rowId);
    const userId = resolveQueueUserId(row?.id ?? null);
    const user = useUser(userId);
    const health = useUserBillingHealth(userId);

    const runAction = async (action: BillingAction) => {
        if (!row || !userId) {
            toast('Unable to run action without a user', { kind: 'danger' });
            return;
        }

        setInflight(action);
        try {
            const result = await apiPost<BillingActionResponse>(
                '/api/admin/billing/health-queue/actions',
                {
                    action,
                    row_id: row.id,
                    issue: row.issue
                }
            );

            if (result.success) {
                toast(`${actionLabel(action)} queued`, {
                    detail:
                        result.status === 'not_supported'
                            ? 'This action is not supported yet'
                            : `User ${result.user_id}`
                });

                await Promise.all([
                    queryClient.invalidateQueries({ queryKey: ['admin', 'billing'] }),
                    queryClient.invalidateQueries({
                        queryKey: ['admin', 'users', 'billing', userId]
                    })
                ]);
            } else {
                toast(`${actionLabel(action)} not applied`, {
                    kind: 'danger',
                    detail: result.error || 'Server did not apply the action'
                });
            }
        } catch (error) {
            toast(`${actionLabel(action)} failed`, {
                kind: 'danger',
                detail: getHttpErrorMessage(error, `Failed to ${actionLabel(action).toLowerCase()}`)
            });
        } finally {
            setInflight(null);
        }
    };

    if (!row) {
        return (
            <div className="detail-panel-header">
                <div className="empty-state">Billing record unavailable.</div>
            </div>
        );
    }

    return (
        <>
            <header className="detail-panel-header">
                <div className="avatar">
                    {row.user_name
                        .split(' ')
                        .map((p) => p[0])
                        .slice(0, 2)
                        .join('')}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <h2 className="name">{row.user_name}</h2>
                    <p className="email">{row.user_email}</p>
                    <span className="uid">Exposure {fmtMoney(row.exposure)}</span>
                </div>
                <button className="close" onClick={onClose} aria-label="Close">
                    <X size={16} />
                </button>
            </header>

            <div className="detail-panel-chips">
                <Chip tone={issueTone(row.issue)}>{row.issue.replace(/_/g, ' ')}</Chip>
                <Chip tone="purple">{row.plan}</Chip>
                <Chip tone="muted">detected {row.detected}</Chip>
            </div>

            <section className="detail-panel-section">
                <h3>Details</h3>
                <p style={{ fontSize: 13, color: 'var(--text-primary)', margin: 0 }}>
                    {row.detail}
                </p>
            </section>

            {(row.issue === 'plan_drift' ||
                row.issue === 'dispute' ||
                row.issue === 'multi_failed') && (
                <section className="detail-panel-section">
                    <h3>Reconciliation source</h3>
                    <p style={{ fontSize: 13, color: 'var(--text-primary)', margin: 0 }}>
                        {row.issue === 'plan_drift'
                            ? 'Compared Stripe webhook subscription payloads with the local subscription row.'
                            : row.issue === 'dispute'
                              ? 'Derived from stored `charge.dispute.created` webhook payloads.'
                              : 'Derived from repeated `invoice.payment_failed` events in the local event log.'}
                    </p>
                </section>
            )}

            {health.data && (
                <section className="detail-panel-section">
                    <h3>Unit economics</h3>
                    <dl className="kv-list">
                        <dt>Stripe sync</dt>
                        <dd>
                            <Chip
                                tone={
                                    health.data.stripe_sync === 'ok'
                                        ? 'success'
                                        : health.data.stripe_sync === 'drifted'
                                          ? 'warning'
                                          : 'danger'
                                }
                            >
                                {health.data.stripe_sync}
                            </Chip>
                        </dd>
                        <dt>Lifetime revenue</dt>
                        <dd>{fmtMoney(health.data.revenue_lifetime)}</dd>
                        <dt>Gross profit</dt>
                        <dd
                            style={{
                                color:
                                    health.data.gross_profit > 0
                                        ? 'var(--success)'
                                        : health.data.gross_profit < 0
                                          ? 'var(--danger)'
                                          : undefined
                            }}
                        >
                            {fmtMoney(health.data.gross_profit)}
                        </dd>
                        {health.data.next_charge_date && (
                            <>
                                <dt>Next charge</dt>
                                <dd>
                                    {fmtMoney(health.data.next_charge_amount ?? 0)} ·{' '}
                                    {health.data.next_charge_date}
                                </dd>
                            </>
                        )}
                    </dl>
                    <div
                        style={{
                            marginTop: 10,
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 6,
                            fontSize: 10
                        }}
                    >
                        {Object.entries(health.data.cost_breakdown).map(([k, v]) => (
                            <div
                                key={k}
                                style={{
                                    padding: '6px 8px',
                                    background: 'var(--bg-input)',
                                    borderRadius: 4
                                }}
                            >
                                <div
                                    style={{
                                        color: 'var(--text-muted)',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.06em',
                                        fontSize: 9,
                                        fontWeight: 600
                                    }}
                                >
                                    {k}
                                </div>
                                <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                                    {fmtMoney(v)}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {user.data && (
                <section className="detail-panel-section">
                    <h3>User</h3>
                    <dl className="kv-list">
                        <dt>Plan</dt>
                        <dd>{user.data.plan}</dd>
                        <dt>App version</dt>
                        <dd>v{user.data.app_version}</dd>
                        <dt>Country</dt>
                        <dd>{user.data.country}</dd>
                        <dt>Last active</dt>
                        <dd>{user.data.last_active}</dd>
                    </dl>
                </section>
            )}

            <footer className="detail-panel-footer">
                <div className="row">
                    <RoleButton
                        scope={actionScope('retry_payment')}
                        className="btn btn-ghost"
                        type="button"
                        disabled={Boolean(inflight)}
                        onClick={() => runAction('retry_payment')}
                    >
                        {inflight === 'retry_payment' ? 'Retrying…' : 'Retry payment'}
                    </RoleButton>
                    <RoleButton
                        scope={actionScope('force_sync')}
                        className="btn btn-ghost"
                        type="button"
                        disabled={Boolean(inflight)}
                        onClick={() => runAction('force_sync')}
                    >
                        {inflight === 'force_sync' ? 'Syncing…' : 'Force sync'}
                    </RoleButton>
                </div>
                <div className="row">
                    <RoleButton
                        scope={actionScope('warn')}
                        className="btn btn-ghost"
                        type="button"
                        disabled={Boolean(inflight)}
                        onClick={() => runAction('warn')}
                    >
                        {inflight === 'warn' ? 'Sending…' : 'Warn overage'}
                    </RoleButton>
                    <RoleButton
                        scope={actionScope('waive')}
                        className="btn btn-ghost"
                        type="button"
                        disabled={Boolean(inflight)}
                        onClick={() => runAction('waive')}
                    >
                        {inflight === 'waive' ? 'Applying…' : 'Waive'}
                    </RoleButton>
                </div>
                <div className="row">
                    <RoleButton
                        scope={actionScope('refund')}
                        className="btn btn-ghost"
                        type="button"
                        disabled={Boolean(inflight)}
                        onClick={() => runAction('refund')}
                    >
                        {inflight === 'refund' ? 'Refunding…' : 'Refund'}
                    </RoleButton>
                    <RoleButton
                        scope={actionScope('downgrade')}
                        className="btn btn-danger"
                        type="button"
                        disabled={Boolean(inflight)}
                        onClick={() => runAction('downgrade')}
                    >
                        {inflight === 'downgrade' ? 'Downgrading…' : 'Downgrade'}
                    </RoleButton>
                </div>
            </footer>
        </>
    );
}
